import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from "lucide-react"
import * as React from "react"

import { Button } from "@/components/ui/button"
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"] as const

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const

/** ISO date string, "YYYY-MM-DD". No time, no timezone. */
export type DateStr = string

function toDate(iso: DateStr | null | undefined): Date | null {
  if (!iso) return null
  const [y, m, d] = iso.split("-").map(Number)
  if (!y || !m || !d) return null
  return new Date(y, m - 1, d)
}

function toIso(d: Date): DateStr {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function isSameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()
}

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1)
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

/**
 * Days shown in a month grid. 6 rows × 7 columns, week starts Monday.
 * Leading/trailing cells belong to neighboring months so the grid never jumps.
 */
function monthGrid(anchor: Date): Date[] {
  const first = startOfMonth(anchor)
  // JS day: 0=Sun … 6=Sat. We want Monday-first, so shift.
  const leading = (first.getDay() + 6) % 7
  const start = new Date(first)
  start.setDate(1 - leading)
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start)
    d.setDate(start.getDate() + i)
    return d
  })
}

/** Format an ISO date for display in the trigger. "Aug 25, 2026". */
export function formatDateShort(iso: DateStr | null | undefined): string {
  const d = toDate(iso)
  if (!d) return ""
  return `${MONTHS[d.getMonth()]!.slice(0, 3)} ${d.getDate()}, ${d.getFullYear()}`
}

export interface CalendarProps {
  /** Currently-selected day, ISO. */
  value?: DateStr | null
  /** Range mode — highlights days between `rangeFrom` and `rangeTo`. */
  rangeFrom?: DateStr | null
  rangeTo?: DateStr | null
  /** Which month the grid renders. Uncontrolled defaults to today or value's month. */
  month?: Date
  onMonthChange?: (m: Date) => void
  onSelect: (iso: DateStr) => void
  /** Ranges beyond which days are disabled. ISO. */
  min?: DateStr
  max?: DateStr
  className?: string
}

export function Calendar({
  value,
  rangeFrom,
  rangeTo,
  month: monthProp,
  onMonthChange,
  onSelect,
  min,
  max,
  className,
}: CalendarProps): JSX.Element {
  const anchor = monthProp ?? toDate(value) ?? toDate(rangeFrom) ?? new Date()
  const [uncontrolled, setUncontrolled] = React.useState<Date>(anchor)
  const month = monthProp ?? uncontrolled
  const setMonth = (m: Date) => {
    if (onMonthChange) onMonthChange(m)
    else setUncontrolled(m)
  }

  const today = new Date()
  const selected = toDate(value)
  const from = toDate(rangeFrom)
  const to = toDate(rangeTo)
  const minDate = toDate(min)
  const maxDate = toDate(max)

  const days = React.useMemo(() => monthGrid(month), [month])

  return (
    <div className={cn("w-[260px] select-none", className)}>
      {/* header */}
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={() => {
            setMonth(addMonths(month, -1))
          }}
          className={cn(
            "grid size-7 place-items-center rounded-md text-text-3",
            "transition-colors duration-120 ease-out",
            "hover:bg-surface-sunken hover:text-text",
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
          )}
          aria-label="Previous month"
        >
          <ChevronLeft className="size-4" aria-hidden="true" />
        </button>
        <div className="font-id text-body-sm font-semibold text-text" aria-live="polite">
          {MONTHS[month.getMonth()]} {month.getFullYear()}
        </div>
        <button
          type="button"
          onClick={() => {
            setMonth(addMonths(month, 1))
          }}
          className={cn(
            "grid size-7 place-items-center rounded-md text-text-3",
            "transition-colors duration-120 ease-out",
            "hover:bg-surface-sunken hover:text-text",
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
          )}
          aria-label="Next month"
        >
          <ChevronRight className="size-4" aria-hidden="true" />
        </button>
      </div>

      {/* weekday header */}
      <div className="mb-1 grid grid-cols-7 gap-0.5">
        {WEEKDAYS.map((w) => (
          <div
            key={w}
            className="grid h-7 place-items-center font-id text-micro uppercase tracking-[0.08em] text-text-3"
          >
            {w}
          </div>
        ))}
      </div>

      {/* day grid — no gap between cells so the range fill reads as one bar */}
      <div role="grid" className="grid grid-cols-7">
        {days.map((d, i) => {
          const iso = toIso(d)
          const inMonth = isSameMonth(d, month)
          const isToday = isSameDay(d, today)
          const isSelected = selected ? isSameDay(d, selected) : false
          const isRangeStart = from ? isSameDay(d, from) : false
          const isRangeEnd = to ? isSameDay(d, to) : false
          const inRange = from && to && d > from && d < to
          const isRangeEdge = isRangeStart || isRangeEnd
          const isRangeSingle = from && to && isSameDay(from, to) && isRangeEdge
          const disabled = (minDate && d < minDate) || (maxDate && d > maxDate) || false

          // Half-width bars so adjacent range cells' fills touch into one bar.
          const showBandLeft = (inRange || (isRangeEnd && !isRangeSingle)) && from && to
          const showBandRight = (inRange || (isRangeStart && !isRangeSingle)) && from && to

          return (
            <div key={i} className="relative">
              {showBandLeft && (
                <span
                  aria-hidden="true"
                  className="absolute inset-y-1 left-0 right-1/2 bg-brand-tint"
                />
              )}
              {showBandRight && (
                <span
                  aria-hidden="true"
                  className="absolute inset-y-1 left-1/2 right-0 bg-brand-tint"
                />
              )}
              <button
                type="button"
                role="gridcell"
                disabled={Boolean(disabled)}
                aria-selected={isSelected || isRangeEdge}
                aria-current={isToday ? "date" : undefined}
                onClick={() => {
                  onSelect(iso)
                }}
                className={cn(
                  "relative grid h-8 w-full place-items-center rounded-md text-body-sm tabular-nums",
                  "transition-colors duration-120 ease-out",
                  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
                  inMonth ? "text-text" : "text-text-disabled",
                  inRange && !isRangeEdge && "text-text",
                  (isSelected || isRangeEdge) &&
                    "bg-brand !text-white font-semibold hover:bg-brand-strong",
                  !isSelected && !isRangeEdge && isToday && "ring-1 ring-inset ring-border-strong",
                  !isSelected && !isRangeEdge && !inRange && !disabled && "hover:bg-surface-sunken",
                  disabled && "cursor-not-allowed opacity-40",
                )}
              >
                {d.getDate()}
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export interface DatePickerProps {
  value: DateStr | null
  onChange: (v: DateStr | null) => void
  placeholder?: string
  min?: DateStr
  max?: DateStr
  disabled?: boolean
  id?: string
  "aria-label"?: string
  className?: string
}

export function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  min,
  max,
  disabled,
  id,
  "aria-label": ariaLabel,
  className,
}: DatePickerProps): JSX.Element {
  const [open, setOpen] = React.useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn(
            "flex h-9 w-full items-center gap-2 rounded-md border border-border bg-surface px-3",
            "text-left text-body transition-colors duration-120 ease-out",
            "hover:border-border-strong",
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
            "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-text-disabled",
            className,
          )}
        >
          <CalendarIcon className="size-4 shrink-0 text-text-3" aria-hidden="true" />
          <span
            className={cn("min-w-0 flex-1 truncate", value ? "text-text" : "text-text-disabled")}
          >
            {value ? formatDateShort(value) : placeholder}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-3">
        <Calendar
          value={value}
          min={min}
          max={max}
          onSelect={(iso) => {
            onChange(iso)
            setOpen(false)
          }}
        />
        <div className="mt-3 flex items-center justify-between border-t border-border-soft pt-2.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onChange(null)
              setOpen(false)
            }}
          >
            Clear
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onChange(toIso(new Date()))
              setOpen(false)
            }}
          >
            Today
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export interface DateRangePickerProps {
  from: DateStr | null
  to: DateStr | null
  onChange: (v: { from: DateStr | null; to: DateStr | null }) => void
  min?: DateStr
  max?: DateStr
  disabled?: boolean
  fromLabel?: string
  toLabel?: string
  className?: string
}

export function DateRangePicker({
  from,
  to,
  onChange,
  min,
  max,
  disabled,
  fromLabel = "From",
  toLabel = "To",
  className,
}: DateRangePickerProps): JSX.Element {
  const [open, setOpen] = React.useState(false)
  const [side, setSide] = React.useState<"from" | "to">("from")

  const openOn = (which: "from" | "to") => {
    setSide(which)
    setOpen(true)
  }

  const handleSelect = (iso: DateStr) => {
    if (side === "from") {
      const nextTo = to && iso > to ? null : to
      onChange({ from: iso, to: nextTo })
      if (!nextTo) {
        setSide("to")
        return
      }
    } else if (from && iso < from) {
      onChange({ from: iso, to: from })
    } else {
      onChange({ from, to: iso })
    }
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className={cn("flex items-center gap-2", className)}>
          <div className="min-w-0 flex-1">
            <PopoverAnchorButton
              onClick={() => {
                openOn("from")
              }}
              active={open && side === "from"}
              value={from}
              placeholder={fromLabel}
              disabled={disabled}
              ariaLabel={fromLabel}
            />
          </div>
          <span className="text-text-3" aria-hidden="true">
            –
          </span>
          <div className="min-w-0 flex-1">
            <PopoverAnchorButton
              onClick={() => {
                openOn("to")
              }}
              active={open && side === "to"}
              value={to}
              placeholder={toLabel}
              disabled={disabled}
              ariaLabel={toLabel}
            />
          </div>
        </div>
      </PopoverAnchor>
      <PopoverContent align="start" className="w-auto p-3">
        <div className="mb-2.5 grid grid-cols-2 gap-1 rounded-md bg-surface-sunken p-0.5">
          <SideTab
            active={side === "from"}
            onClick={() => setSide("from")}
            label="From"
            value={from}
          />
          <SideTab active={side === "to"} onClick={() => setSide("to")} label="To" value={to} />
        </div>
        <Calendar
          rangeFrom={from}
          rangeTo={to}
          value={side === "from" ? from : to}
          min={min}
          max={max}
          onSelect={handleSelect}
        />
        <div className="mt-3 flex items-center justify-between border-t border-border-soft pt-2.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onChange({ from: null, to: null })
              setSide("from")
              setOpen(false)
            }}
          >
            Clear
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setOpen(false)
            }}
          >
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

function SideTab({
  active,
  onClick,
  label,
  value,
}: {
  active: boolean
  onClick: () => void
  label: string
  value: DateStr | null
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex flex-col items-start rounded-sm px-2.5 py-1.5 text-left",
        "transition-colors duration-120 ease-out",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
        active ? "bg-surface shadow-e1" : "hover:text-text",
      )}
    >
      <span
        className={cn(
          "font-id text-micro uppercase tracking-[0.10em]",
          active ? "text-text-3" : "text-text-3",
        )}
      >
        {label}
      </span>
      <span className={cn("text-caption tabular-nums", value ? "text-text" : "text-text-disabled")}>
        {value ? formatDateShort(value) : "Pick a date"}
      </span>
    </button>
  )
}

function PopoverAnchorButton({
  onClick,
  active,
  value,
  placeholder,
  disabled,
  ariaLabel,
}: {
  onClick: () => void
  active: boolean
  value: DateStr | null
  placeholder: string
  disabled?: boolean
  ariaLabel: string
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(
        "flex h-9 w-full items-center gap-2 rounded-md border bg-surface px-3",
        "text-left text-body-sm transition-colors duration-120 ease-out",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
        "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-text-disabled",
        active ? "border-brand" : "border-border hover:border-border-strong",
      )}
    >
      <CalendarIcon className="size-3.5 shrink-0 text-text-3" aria-hidden="true" />
      <span className={cn("min-w-0 flex-1 truncate", value ? "text-text" : "text-text-disabled")}>
        {value ? formatDateShort(value) : placeholder}
      </span>
    </button>
  )
}
